import { Box } from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { RiArchiveDrawerLine } from 'react-icons/ri';
import {
  MdApartment,
  MdContentCopy,
  MdOutlineStarPurple500,
  MdCallMerge,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdOutlineBarChart,
  MdOutlineExitToApp,
  MdOutlineSearch,
  MdOutlineSettings,
  MdDisplaySettings,
  // MdOutlineBadge,
  MdCloudSync,
  // MdSend,
} from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router';
import { useStoreState } from 'src/reducers';
import { isAdminLevelRole } from 'src/utils/roles';

import useWindowResize from '../hooks/useWindowResize';

const navContainerSx = {
  display: 'grid',
};

const getToggleSx = (collapsed: boolean) => ({
  height: '2rem',
  display: 'none',
  justifyContent: 'center',
  padding: 0,
  alignItems: 'center',
  fontSize: '20px',
  cursor: 'pointer',
  margin: '-1rem 0 0',
  borderBottom: '1px solid var(--ouster-colors-gray-700)',
  '@media (max-width: 1400px)': {
    display: 'flex',
    justifyContent: collapsed ? 'center' : 'flex-end',
    paddingRight: collapsed ? 0 : '1rem',
    backgroundColor: '#5e79fb24',
  },
});

const getActionButtonSx = (
  collapsed: boolean,
  variant: 'solid' | 'outline'
) => ({
  textAlign: 'left',
  display: 'flex',
  fontSize: '14px',
  marginInline: '1rem',
  columnGap: '0.5rem',
  borderRadius: '4px',
  alignItems: 'center',
  padding: '0.5rem 0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  lineHeight: 1,
  backgroundColor:
    variant === 'solid' ? 'var(--color-blast-off-blue)' : 'transparent',
  border: '1px solid',
  borderColor:
    variant === 'solid' ? 'transparent' : 'var(--color-blast-off-blue)',
  color: variant === 'solid' ? 'white' : 'var(--color-blast-off-blue)',
  '& svg': {
    fontSize: '24px',
  },
  '&:hover': {
    filter: 'brightness(130%)',
    borderColor: 'var(--ouster-colors-gray-300)',
    boxShadow: '0 0 0 2px var(--ouster-colors-gray-700)',
  },
  '@media (max-width: 1400px)': {
    '& span': {
      display: collapsed ? 'none' : 'flex',
    },
  },
});

const getNavLinkSx = (
  isActive: boolean,
  collapsed: boolean,
  options?: { isSubmenu?: boolean }
) => ({
  textAlign: 'left',
  display: 'flex',
  fontSize: '14px',
  columnGap: '0.5rem',
  alignItems: 'center',
  padding: options?.isSubmenu
    ? '0.75rem 1.75rem 0.75rem 2.75rem'
    : '0.85rem 1.75rem',
  borderBottom: '1px solid var(--ouster-colors-whiteAlpha-200)',
  fontWeight: options?.isSubmenu ? 400 : 500,
  whiteSpace: 'nowrap',
  cursor: isActive ? 'default' : 'pointer',
  lineHeight: 1,
  backgroundColor: isActive ? 'var(--ouster-colors-blue-700)' : 'transparent',
  color: isActive ? 'white' : 'var(--ouster-colors-gray-300)',
  '& svg': {
    fontSize: '24px',
  },
  '&:hover': {
    backgroundColor: isActive
      ? 'var(--ouster-colors-blue-700)'
      : 'var(--ouster-colors-gray-800)',
    color: isActive ? 'white' : 'var(--color-blast-off-blue)',
  },
  '@media (max-width: 1400px)': {
    '& span': {
      display: collapsed ? 'none' : 'flex',
    },
    ...(options?.isSubmenu
      ? {
          paddingLeft: collapsed ? '1.75rem' : '2.75rem',
        }
      : {}),
  },
});

const getSectionHeaderSx = (collapsed: boolean) => ({
  textAlign: 'left',
  display: 'flex',
  fontSize: '11px',
  columnGap: '0.5rem',
  alignItems: 'center',
  padding: '1rem 1.75rem 0.5rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  lineHeight: 1,
  color: 'var(--ouster-colors-gray-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  '& svg': {
    fontSize: '18px',
  },
  '@media (max-width: 1400px)': {
    display: collapsed ? 'none' : 'flex',
  },
});

interface Tab {
  name: string;
  submenus?: {
    icon: JSX.Element;
    name: string;
    path: string;
  }[];
}

/**
 * AdminSideNav component for admin-specific navigation
 */
export default function AdminSideNav() {
  const { appUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  const navigate = useNavigate();
  const location = useLocation();

  const { width } = useWindowResize();
  const [collapsed, setCollapsed] = useState(width <= 1400);

  // Get cloud team emails from env
  const cloudTeamEmails = useMemo(() => {
    const emails = process.env.REACT_APP_CLOUD_TEAM || '';
    return emails.split(',').map((email) => email.trim());
  }, []);

  // Check if current user is part of cloud team
  // Depend on the whole `appUser` object (not just appUser?.email): the
  // compiler can only track reactivity at the object-property-access level
  // it observes here, so a narrower dep array risks the memo going stale
  // if appUser's identity changes without the email string changing.
  const isCloudTeamMember = useMemo(
    () => (appUser?.email ? cloudTeamEmails.includes(appUser.email) : false),
    [appUser, cloudTeamEmails]
  );

  const isAdminLevel = useMemo(
    () => isAdminLevelRole(curUserOrg?.role),
    [curUserOrg?.role]
  );

  const tabs: Tab[] = useMemo(
    () => [
      {
        name: 'Insights',
        submenus: [
          {
            name: 'Growth Trends',
            path: '/admin_dashboard/insights/growth_trends',
            icon: <MdOutlineBarChart />,
          },
        ],
      },
      {
        name: 'User Management',
        submenus: [
          {
            name: 'User Search',
            path: '/admin_dashboard/user_management/user_search',
            icon: <MdOutlineSearch />,
          },
          // hide Role Permissions for now since the page is not ready and we don't want to surface an empty page
          // {
          //   name: 'Role Permissions',
          //   path: '/admin_dashboard/user_management/role_permissions',
          //   icon: <MdOutlineBadge />,
          // },
        ],
      },
      {
        name: 'Organization Hub',
        submenus: [
          ...(isAdminLevel
            ? [
                {
                  name: 'Org Overview',
                  path: '/admin_dashboard/organization_hub/org_overview',
                  icon: <MdApartment />,
                },
              ]
            : []),
          {
            name: 'Provisioning',
            path: '/admin_dashboard/organization_hub/provisioning',
            icon: <MdCloudSync />,
          },
          {
            name: 'Merge',
            path: '/admin_dashboard/organization_hub/merge',
            icon: <MdCallMerge />,
          },
        ],
      },
      {
        name: 'Recording Controls',
        submenus: [
          ...(isAdminLevel
            ? [
                {
                  name: 'Recording Lookup',
                  path: '/admin_dashboard/recording_controls/recording_lookup',
                  icon: <MdOutlineSearch />,
                },
              ]
            : []),

          {
            name: 'Copied Recordings',
            path: '/admin_dashboard/recording_controls/copied_recordings',
            icon: <MdContentCopy />,
          },
          {
            name: 'Saved Recordings',
            path: '/admin_dashboard/recording_controls/saved_recordings',
            icon: <MdOutlineStarPurple500 />,
          },
          // {
          //   name: 'Distribution',
          //   path: '/admin_dashboard/recording_controls/distribution',
          //   icon: <MdSend />,
          // },
        ],
      },
      // Only show Developer Tools if user is part of cloud team
      ...(isCloudTeamMember && isAdminLevel
        ? [
            {
              name: 'Developer Tools',
              submenus: [
                {
                  name: 'Recordings Mgmt',
                  path: '/admin_dashboard/developer_tools/recordings_mgmt',
                  icon: <RiArchiveDrawerLine />,
                },
                {
                  name: 'OSD Mgmt',
                  path: '/admin_dashboard/developer_tools/osd_mgmt',
                  icon: <MdDisplaySettings />,
                },
                {
                  name: 'Configuration',
                  path: '/admin_dashboard/developer_tools/configuration',
                  icon: <MdOutlineSettings />,
                },
              ],
            },
          ]
        : []),
    ],
    [isAdminLevel, isCloudTeamMember]
  );

  useEffect(() => {
    // `collapsed` is user-toggleable (see handleToggle) and must stay
    // "sticky" to that manual choice across re-renders at the same width,
    // so it can't be a plain derived value - but it also needs to
    // re-sync to the browser's viewport width (an external signal)
    // whenever that width crosses the collapse breakpoint.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCollapsed(width <= 1400);
  }, [width]);

  function handleToggle() {
    setCollapsed(!collapsed);
  }

  function handleExitAdminMode() {
    // Navigate back to the regular drive/private view
    navigate('/drive/private');
  }

  function handleNavClick(path: string) {
    navigate(path);
  }

  function isPathActive(path: string, submenus?: any[]): boolean {
    const isSubmenuActive =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      submenus?.some((submenu) => location.pathname === submenu.path) ?? false;

    if (collapsed) {
      // If collapsed, consider the menu active when it matches path or has active submenu
      return location.pathname === path || isSubmenuActive;
    }

    // If expanded, consider menu active only when the path match
    return location.pathname === path;
  }

  return (
    <Box as="nav" sx={navContainerSx} data-testid="admin-side-nav-root">
      <Box
        as="button"
        type="button"
        onClick={handleToggle}
        sx={getToggleSx(collapsed)}
        data-testid="admin-side-nav-toggle"
      >
        {collapsed ? (
          <MdKeyboardDoubleArrowRight />
        ) : (
          <MdKeyboardDoubleArrowLeft />
        )}
      </Box>

      {/* EXIT ADMIN MODE BUTTON */}
      <Box
        display="grid"
        rowGap="1rem"
        mt="1rem"
        pb="1.5rem"
        borderBottom="1px solid"
        borderColor="var(--color-unify-white-30)"
        data-testid="admin-side-nav-function-buttons"
      >
        <Tooltip
          title={
            collapsed ? (
              <div style={{ fontSize: 14, padding: 4 }}>Exit Admin Mode</div>
            ) : null
          }
          arrow
          placement="right"
          sx={{ fontSize: 34 }}
        >
          <Box
            as="button"
            type="button"
            sx={getActionButtonSx(collapsed, 'solid')}
            onClick={handleExitAdminMode}
            data-testid="admin-side-nav-exit-btn"
          >
            <MdOutlineExitToApp />
            <span>Exit Admin Mode</span>
          </Box>
        </Tooltip>
      </Box>

      {/* NAV SECTIONS */}
      {tabs.map((tab) => (
        <div
          key={tab.name}
          data-testid={`admin-side-nav-tab-${tab.name
            .replace(/\s+/g, '-')
            .toLowerCase()}`}
        >
          {/* Section Header */}

          <Box
            sx={getSectionHeaderSx(collapsed)}
            data-testid={`admin-side-nav-header-${tab.name
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
          >
            <span>{tab.name}</span>
          </Box>
          {/* Render submenus */}
          {tab.submenus && (
            <div
              data-testid={`admin-side-nav-submenus-${tab.name
                .replace(/\s+/g, '-')
                .toLowerCase()}`}
            >
              {tab.submenus.map((submenu) => (
                <Tooltip
                  key={submenu.name}
                  title={
                    collapsed ? (
                      <div style={{ fontSize: 14, padding: 4 }}>
                        {submenu.name}
                      </div>
                    ) : null
                  }
                  arrow
                  placement="right"
                  sx={{ fontSize: 34 }}
                >
                  <Box
                    as="a"
                    sx={getNavLinkSx(isPathActive(submenu.path), collapsed, {
                      isSubmenu: true,
                    })}
                    onClick={() => handleNavClick(submenu.path)}
                    data-testid={`admin-side-nav-sublink-${submenu.name
                      .replace(/\s+/g, '-')
                      .toLowerCase()}`}
                  >
                    {submenu.icon}
                    <span>{submenu.name}</span>
                  </Box>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      ))}
    </Box>
  );
}
